import React, { useState, useEffect } from 'react';
import { Lock, LogIn, X, Sparkles, Loader, ArrowLeft, ArrowRight, SlidersHorizontal, MapPin, Palette, Check } from 'lucide-react';
import { Button } from "./button";
import { Card, CardContent, CardHeader, CardTitle } from "./card";
import { useNavigate } from 'react-router-dom';
import PlaygroundMapComponent from './PlaygroundMapComponent';
import StaticImageSlideshow from './staticimageslideshow';
import ColorSettingsPopup from './ColorSettingsPopup';
import ColorLegend from './ColorLegend';
import PlaygroundImagePreview from './PlaygroundImagePreview';
import { useToast } from './use-toast';
import { FORMATS } from '../config/prices';
import { trackPlaygroundEvents } from '../utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Sample playground areas with demo activities
const PLAYGROUND_AREAS = {
  munich: {
    name: 'Munich, Germany',
    coordinates: [48.1351, 11.5820],
    sampleActivities: [
    ]
  },
  innsbruck: {
    name: 'Innsbruck, Austria',
    coordinates: [47.2625, 11.4011],
    sampleActivities: [
    ]
  },
  bregenz: {
    name: 'Bregenz, Austria',
    coordinates: [47.5031, 9.7472],
    sampleActivities: [
    ]
  },
  laspezia: {
    name: 'La Spezia, Italy',
    coordinates: [44.0999, 9.8166],
    sampleActivities: [
    ]
  }
};

const DEFAULT_SETTINGS = {
  km2: 50,
  figure_width: 14,
  figure_height: 10.5,
  placeName: 'Munich, Germany',
  activities: { color: 'sienna', alpha: 0.5, active: true, lineWidth: "normal" },
  buildings: { color: 'darkgrey', alpha: 0.8, active: true },
  forests: { color: 'forestgreen', alpha: 0.1, active: true },
  naturalareas: { color: 'darkgreen', alpha: 0.2, active: true },
  water: { color: 'cornflowerblue', alpha: 0.4, active: true },
  oceans: { color: 'royalblue', alpha: 0.4, active: true },
  streets: { color: 'black', alpha: 0.2, active: true, details: 'roads' },
  border: { color: 'black', alpha: 1, active: false, lineWidth: "normal" },
  land: { color: 'palegoldenrod', alpha: 0.15, active: true },
  topography: true
};

// Default format for playground (4:3 aspect ratio)
const DEFAULT_FORMAT = {
  value: '4:3',
  size: 'XS',
  label: '4:3 XS',
  dimensions: { width: 14, height: 10.5 },
  aspectRatio: 1.333
};

export default function Playground() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedArea, setSelectedArea] = useState('munich');
  const [showOverlay, setShowOverlay] = useState(true);
  const [mapCenter, setMapCenter] = useState(PLAYGROUND_AREAS['munich'].coordinates);
  const [rectangleBounds, setRectangleBounds] = useState(
      [[48.13341414862261, 11.558022777014799], [48.18848604878309, 11.66810484749692]]
  );
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(DEFAULT_FORMAT);
  const [isPortrait, setIsPortrait] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  // trackPlaygroundEvents.playgroundEntered();

  const handleAreaChange = (areaKey) => {
    setSelectedArea(areaKey);
    const area = PLAYGROUND_AREAS[areaKey];
    setMapCenter(area.coordinates);

    // Set initial bounds based on area
    let newBounds;
    let placeName;
    if (areaKey === 'innsbruck') {
      newBounds = [[47.21773593542635, 11.299059033639262], [47.31312326457366, 11.486477966360741]];
      setRectangleBounds(newBounds);
      placeName = 'Innsbruck, Austria';
      setSettings(prev => ({
        ...prev,
        km2: 150
      }));
    } else if (areaKey === 'bregenz') {
      newBounds = [[47.47810157913538, 9.591991218601807], [47.57348890828269, 9.780338493312255]];
      setRectangleBounds(newBounds);
      placeName = 'Bregenz, Austria';
      setSettings(prev => ({
        ...prev,
        km2: 150
      }));
    } else if (areaKey === 'laspezia') {
      newBounds = [[44.02038581818282, 9.776188444698455], [44.11577314733013, 9.953196931278113]];
      setRectangleBounds(newBounds);
      placeName = 'La Spezia, Italy';
      setSettings(prev => ({
        ...prev,
        km2: 250
      }));
    } else {
      newBounds = [[48.13341414862261, 11.558022777014799], [48.18848604878309, 11.66810484749692]];
      setRectangleBounds(newBounds);
      setSettings(prev => ({
        ...prev,
        km2: 50
      }));
      placeName = 'Munich, Germany';
    }

    // Update placeName in settings
    setSettings(prev => ({
      ...prev,
      placeName: placeName
    }));

    // Update format based on new bounds
    // updateFormatForArea(newBounds);
  };

  const handleColorSettingChange = (key, subKey, value) => {
    setSettings(prevSettings => ({
      ...prevSettings,
      [key]: {
        ...prevSettings[key],
        [subKey]: value
      }
    }));
  };

  // Initialize format on component mount
  useEffect(() => {
    // Calculate format for initial Innsbruck bounds
    const initialBounds = [[48.01886600568445, 11.434305849183788], [48.11425333483176, 11.62462359417559]];
    // updateFormatForArea(initialBounds);
  }, []);

  // Cleanup image URL on unmount
  useEffect(() => {
    return () => {
      if (generatedImageUrl) {
        URL.revokeObjectURL(generatedImageUrl);
      }
    };
  }, []);

  const handleLoginClick = () => {
    trackPlaygroundEvents.playgroundConvertedToSignup();
    navigate('/login');
  };

  const handleGenerateClick = async () => {
    setIsGenerating(true);
    try {
      const area = PLAYGROUND_AREAS[selectedArea];

      // Get XS dimensions from selectedFormat
      const xsDimensions = selectedFormat.dimensions;
      const finalDimensions = isPortrait
        ? { figure_width: xsDimensions.height, figure_height: xsDimensions.width }
        : { figure_width: xsDimensions.width, figure_height: xsDimensions.height };
      const requestData = {
        settings: {
          ...settings,
          ...finalDimensions
        },
        filteredActivities: area.sampleActivities,
        selectedFormat: selectedFormat,
        rectangleBounds,
        allDates: true,
        dateFrom: null,
        dateTo: null,
        topography: settings.topography || true,
        frame: false,
        frameSettings: {},
        activitySource: 'demo',
        dpi: 250,
        free: true
      };
      const response = await fetch(`${API_URL}/playground/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setGeneratedImageUrl(url);
        trackPlaygroundEvents.playgroundMapGenerated(selectedArea);
        toast({
          title: "Map generated successfully",
          description: "Your demo map has been generated. Click the expand button to view it in full size.",
          duration: 5000,
        });
      } else {
        trackPlaygroundEvents.playgroundMapError(selectedArea);
        toast({
          title: "Error",
          description: 'Failed to generate your map. Please try again.',
          variant: "destructive",
        });
      }
    } catch (error) {
      trackPlaygroundEvents.playgroundMapError(selectedArea);
      // console.error('Generation error:', error);
      toast({
        title: "Error",
        description: 'An error occurred while generating your map.',
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const STEPS = [
    { id: 1, label: 'Choose Location', icon: MapPin },
    { id: 2, label: 'Choose Style', icon: Palette },
    { id: 3, label: 'Generate', icon: Sparkles },
  ];

  const goToStep = (n) => {
    setCustomizeOpen(false);
    setExpandedCategory(null);
    setCurrentStep(n);
  };

  return (
    <div className="w-full flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Main Content - Blurred when overlay is shown */}
      <div className={`container mx-auto max-w-7xl px-4 py-6 transition-all ${showOverlay ? 'blur-sm pointer-events-none' : ''}`}>
        <div className="flex flex-col gap-6">
          {/* Welcome heading — compact */}
          <div className="text-center">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 tracking-tight">
              Map Prints Playground
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Pick a demo area, choose a style, and generate a sample map.
            </p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-2 md:gap-4">
              {STEPS.map((step, idx) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isDone = currentStep > step.id;
                return (
                  <React.Fragment key={step.id}>
                    <button
                      type="button"
                      onClick={() => !showOverlay && goToStep(step.id)}
                      disabled={showOverlay}
                      className={`flex items-center gap-2 rounded-full px-3 md:px-4 py-2 transition-colors text-sm font-medium ${
                        isActive
                          ? 'bg-gray-900 text-white shadow-lg'
                          : isDone
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <span className={`flex items-center justify-center h-6 w-6 rounded-full ${
                        isActive ? 'bg-white text-gray-900' : isDone ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                      </span>
                      <span className="hidden sm:inline">{step.label}</span>
                    </button>
                    {idx < STEPS.length - 1 && (
                      <div className={`h-0.5 w-6 md:w-12 rounded-full ${currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Step 1 — Choose Location */}
          {currentStep === 1 && (
            <Card className="border-0 rounded-2xl shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg md:text-xl text-center">Step 1 · Choose a location</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="mx-auto w-full max-w-2xl flex flex-col gap-4">
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden h-80">
                    <PlaygroundMapComponent
                      mapCenter={mapCenter}
                      zoom={10}
                      rectangleBounds={rectangleBounds}
                    />
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-xs text-center text-amber-800">
                      Only preselected areas can be used in the playground.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(PLAYGROUND_AREAS).map(([key, area]) => (
                      <button
                        key={key}
                        onClick={() => !showOverlay && handleAreaChange(key)}
                        disabled={showOverlay}
                        className={`p-3 rounded-xl border-2 transition-colors font-medium text-sm text-center ${
                          selectedArea === key
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        } ${showOverlay ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {area.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button
                    onClick={() => goToStep(2)}
                    variant="black"
                    size="lg"
                    disabled={showOverlay}
                  >
                    Next: Choose a Style
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2 — Choose Style */}
          {currentStep === 2 && (
            <Card className="border-0 rounded-2xl shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg md:text-xl text-center">Step 2 · Choose a style</CardTitle>
              </CardHeader>
              <CardContent>
                {customizeOpen ? (
                  <div className="flex flex-col">
                    <div className="flex items-center mb-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (expandedCategory) {
                            setExpandedCategory(null);
                          } else {
                            setCustomizeOpen(false);
                          }
                        }}
                        className="flex items-center gap-1 text-gray-700 hover:bg-gray-200 rounded-xl"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                      </Button>
                    </div>
                    <div className="md:max-h-[calc(3*10rem)] lg:max-h-[calc(4*10rem)] md:overflow-y-auto pr-1">
                      <ColorSettingsPopup
                        settings={settings}
                        onColorSettingChange={handleColorSettingChange}
                        triggerButton={false}
                        selectedCategory={expandedCategory}
                        onSelectedCategoryChange={setExpandedCategory}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 mx-auto w-full max-w-2xl">
                    <StaticImageSlideshow
                      onSettingsChange={(newSettings) => {
                        setSettings(prevSettings => ({
                          ...prevSettings,
                          ...newSettings
                        }));
                      }}
                    >
                      <Button
                        size="sm"
                        onClick={() => setCustomizeOpen(true)}
                        className="text-xs lg:text-md rainbow-border-bg text-white hover:bg-gray-600"
                      >
                        <SlidersHorizontal className="h-4 w-4 mr-2" />
                        Customize
                      </Button>
                    </StaticImageSlideshow>

                    <ColorLegend
                      settings={settings}
                      direction="horizontal"
                      onOpenCategory={(category) => {
                        setExpandedCategory(category);
                        setCustomizeOpen(true);
                      }}
                    />
                  </div>
                )}
                <div className="flex justify-between pt-6">
                  <Button
                    onClick={() => goToStep(1)}
                    variant="outline"
                    size="lg"
                  >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Back
                  </Button>
                  <Button
                    onClick={() => goToStep(3)}
                    variant="black"
                    size="lg"
                  >
                    Next: Generate
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 3 — Generate */}
          {currentStep === 3 && (
            <Card className="border-0 rounded-2xl shadow-xl">
              <CardHeader>
                <CardTitle className="text-lg md:text-xl text-center">Step 3 · Generate your map</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center gap-6">
                <div className="text-sm text-gray-600 text-center max-w-md">
                  <p>Ready to render <span className="font-semibold">{settings.placeName}</span>?</p>
                  <p>Click below to generate your demo map.</p>
                </div>

                <Button
                  onClick={handleGenerateClick}
                  variant="black"
                  size="lg"
                  disabled={showOverlay || isGenerating}
                  className="w-full max-w-xs disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <Loader className="mr-2 h-5 w-5 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      Generate Demo Map
                    </>
                  )}
                </Button>

                {(isGenerating || generatedImageUrl) && (
                  <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
                    {isGenerating && !generatedImageUrl ? (
                      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-8 text-center shadow-lg">
                        <Loader className="h-10 w-10 animate-spin mx-auto mb-4 text-blue-600" />
                        <p className="text-lg font-semibold text-blue-800 mb-1">Standby...</p>
                        <p className="text-sm text-blue-700">Your image is being rendered. This usually takes a few seconds.</p>
                      </div>
                    ) : (
                      generatedImageUrl && (
                        <PlaygroundImagePreview
                          imageUrl={generatedImageUrl}
                          onClose={() => setGeneratedImageUrl(null)}
                          isLoading={isGenerating}
                        />
                      )
                    )}
                  </div>
                )}

                <div className="flex justify-start w-full pt-2">
                  <Button
                    onClick={() => goToStep(2)}
                    variant="outline"
                    size="lg"
                  >
                    <ArrowLeft className="mr-2 h-5 w-5" />
                    Back
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

        </div>
      </div>

      {/* Overlay Modal */}
      {showOverlay && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-xl flex items-center justify-center z-50">
          <div className="relative">
            <button
              onClick={() => setShowOverlay(false)}
              className="absolute -top-12 right-0 p-2 hover:bg-gray-600 rounded-full transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-6 w-6 text-white hover:text-gray-300" />
            </button>
            <Card className="max-w-md bg-white border-orange-300 rounded-xl">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <Lock className="h-12 w-12 text-orange-500" />
                </div>
                <CardTitle className="text-xl text-orange-600">Full Features Locked</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-700 text-center">
                  You're in playground mode! This is a limited demo with sample data. To create and modify your own maps, you need to log in.
                </p>
                <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-1">
                  <p className="text-xs font-semibold text-gray-700">What you can do:</p>
                  <p className="text-xs text-gray-600">✓ View sample maps</p>
                  <p className="text-xs text-gray-600">✓ Test different colors and styles</p>
                  <p className="text-xs font-semibold text-gray-700 mt-2">What requires login:</p>
                  <p className="text-xs text-gray-600">✗ Create your own maps</p>
                  <p className="text-xs text-gray-600">✗ Connect to Strava/Garmin</p>
                  <p className="text-xs text-gray-600">✗ Download and purchase</p>
                </div>
                <Button
                  onClick={() => setShowOverlay(false)}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Explore Playground
                </Button>
                <Button
                  onClick={handleLoginClick}
                  variant="outline"
                  className="w-full"
                >
                  <LogIn className="mr-2 h-4 w-4" />
                  Login / Register
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
